import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { parentPath } from "../../boundaries/url-path.parser";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";
import {
  normalisedTitle,
  readsAsGenericTitle,
  titleSimilarity,
} from "./boundaries/page-title.search";
import type { MapCurrentGroup, MapPage } from "./mapTypes";

export function overlappingPages(
  group: MapCurrentGroup,
  data: OverviewResponse,
): { pages: MapPage[]; reason: string } | null {
  const buckets = new Map<string, MapPage[]>();
  for (const fact of group.facts) {
    const display = pageDisplayLabel(fact.page, data.site);
    const title = normalisedTitle(display);
    if (title.length < 8 || readsAsGenericTitle(display)) continue;
    buckets.set(title, [...(buckets.get(title) ?? []), fact.page]);
  }
  const exact = [...buckets.values()].find((pages) => pages.length > 1);
  if (exact) {
    return {
      pages: exact,
      reason: "The pages share the same meaningful client-facing title.",
    };
  }
  for (let index = 0; index < group.facts.length; index += 1) {
    const left = group.facts[index]?.page;
    if (!left) continue;
    for (let other = index + 1; other < group.facts.length; other += 1) {
      const right = group.facts[other]?.page;
      if (!right) continue;
      const leftTitle = pageDisplayLabel(left, data.site);
      const rightTitle = pageDisplayLabel(right, data.site);
      const similarity = titleSimilarity(leftTitle, rightTitle);
      if (
        parentPath(left.path) === parentPath(right.path) &&
        similarity.shared >= 3 &&
        similarity.ratio >= 0.8 &&
        !readsAsGenericTitle(leftTitle) &&
        !readsAsGenericTitle(rightTitle)
      ) {
        return {
          pages: [left, right],
          reason:
            "The pages share a URL stem and several distinctive title terms.",
        };
      }
    }
  }
  return null;
}
